"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Button, buttonVariants } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";

type GoogleReviewsPayload = {
  rating: number | null;
  userRatingsTotal: number | null;
};

function useGoogleReviews() {
  const [data, setData] = useState<GoogleReviewsPayload | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchReviews = async () => {
      try {
        const response = await fetch("/api/google-reviews");
        if (!response.ok) return;
        const payload = (await response.json()) as GoogleReviewsPayload;
        if (isMounted) {
          setData(payload);
        }
      } catch {
        // Silently fail, keep default rating
      }
    };
    fetchReviews();
    return () => {
      isMounted = false;
    };
  }, []);

  return {
    displayRating: data?.rating?.toFixed(1) ?? "4.7",
    reviewCount: data?.userRatingsTotal ?? 7000,
  };
}

export function GoogleReviewsHeroBadge() {
  const t = useTranslations("sevillaPage");
  const { displayRating, reviewCount } = useGoogleReviews();

  return (
    <div className="mt-6 rounded-2xl bg-white/95 px-6 py-4 text-ink shadow-xl">
      <div className="flex items-center justify-center gap-2">
        <div className="relative h-6 w-28">
          <svg viewBox="0 0 140 24" className="h-full w-full" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((index) => (
              <path
                key={index}
                d="M12 2l2.4 7.4h7.6l-6 4.6 2.4 7.4-6-4.6-6 4.6 2.4-7.4-6-4.6h7.6z"
                transform={`translate(${index * 28}, 0)`}
                fill="#FFC107"
              />
            ))}
          </svg>
        </div>
        <span className="text-xs sm:text-sm font-semibold text-brand-navy">
          {t("reviews.ratingLabel", { rating: displayRating })}
        </span>
      </div>
      <p className="mt-2 text-[11px] sm:text-xs text-gray-600">
        {t("reviews.countLabel", { count: reviewCount.toLocaleString() })}
      </p>
    </div>
  );
}

export function GoogleReviewsSection() {
  const t = useTranslations("sevillaPage");
  const { displayRating, reviewCount } = useGoogleReviews();
  const reviewsBullets = t.raw("reviewsSection.bullets") as string[];

  return (
    <section className="px-4 section-y bg-white dark:bg-surface-dark">
      <div className="mx-auto max-w-6xl">
        <h3 className="text-center text-2xl sm:text-3xl font-black text-brand-navy dark:text-white">
          {t("reviewsSection.title")}
        </h3>
        <div className="mt-8 grid gap-8 lg:grid-cols-2 items-center rounded-card bg-gradient-to-br from-white to-gray-50 px-8 py-8 shadow-2xl dark:from-surface-dark-raised dark:to-[#1a2332]">
          <div className="flex flex-col items-center gap-4">
            <div className="relative h-10 w-48">
              <svg viewBox="0 0 240 40" className="h-full w-full" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((index: number) => (
                  <path
                    key={index}
                    d="M20 3.33l4 12.34h13l-10.5 7.66 4 12.34L20 27.67l-10.5 7.66 4-12.34L3 15.67h13z"
                    transform={`translate(${index * 48}, 0)`}
                    fill="#FFC107"
                  />
                ))}
              </svg>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-brand-navy dark:text-white">
                {t("reviewsSection.scoreLabel", { rating: displayRating })}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t("reviewsSection.ratingCaption")}</p>
              <p className="mt-2 text-lg font-semibold text-gray-700 dark:text-gray-200">
                {t("reviewsSection.countLabel", { count: reviewCount.toLocaleString() })}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div>
              <h4 className="text-lg font-bold text-brand-navy dark:text-white">{t("reviewsSection.headline")}</h4>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t("reviewsSection.body", { count: reviewCount.toLocaleString() })}
              </p>
            </div>
            <div className="space-y-3">
              {reviewsBullets.map((bullet, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{bullet}</span>
                </div>
              ))}
            </div>
            <Button asChild className={buttonVariants({ variant: "pillNavy", size: "pill", className: "mt-2" })}>
              <Link href="https://www.google.com/search?q=geesol+rese%C3%B1as" target="_blank" rel="noopener noreferrer">
                {t("reviewsSection.cta")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
